/**
 *  @class
 *  @function SlideShow
 */
if (!customElements.get('slide-show')) {
  class SlideShow extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      if (this._flkty || this._stylesPending) return;
      const slideshow = this,
        dataset = slideshow.dataset;

      // WebKit (Safari, and every browser on iOS) can run the deferred
      // scripts before a section's own stylesheet, in <body>, has loaded.
      // Flickity would size the carousel from the unstyled slides, and a
      // slide's height: 100% of the viewport it sized keeps that height, so
      // the carousel starts once the sheet is in. Window load covers a sheet
      // that failed before this ran.
      const section = slideshow.closest('.shopify-section');
      const pending = section && document.readyState !== 'complete' ? Array.from(section.querySelectorAll('link[rel="stylesheet"]')).filter((link) => !link.sheet) : [];
      if (pending.length) {
        this._stylesPending = true;
        const loaded = pending.map((link) => new Promise((resolve) => {
          link.addEventListener('load', resolve, { once: true });
          link.addEventListener('error', resolve, { once: true });
        }));
        const pageLoaded = new Promise((resolve) => window.addEventListener('load', resolve, { once: true }));
        Promise.race([Promise.all(loaded), pageLoaded]).then(() => {
          this._stylesPending = false;
          if (this.isConnected) this.connectedCallback();
        });
        return;
      }

      const slideshow_slides = Array.from(slideshow.querySelectorAll('.carousel__slide'));

      // Single early-return guard
      if (slideshow_slides.length < 1) return;

      const reduce = theme.reducedMotion;

      // Reduced motion: YouTube/Vimeo backgrounds go, so the slide's image
      // shows. They sit under pointer-events: none, so a click-to-play player
      // could never be started. Hosted videos stay, paused on their poster.
      if (reduce) {
        slideshow.querySelectorAll('.slideshow__slide-video-bg iframe').forEach((iframe) => {
          iframe.closest('.slideshow__slide-video-bg').remove();
        });
      }

      const dots_enabled = dataset.dots === 'true',
        autoplay = (Shopify.designMode || reduce) ? false : (dataset.autoplay == 'false' ? false : parseInt(dataset.autoplay, 10)),
        // AUTHENTICA: start-align carousels in RTL — 2026-10-06. Flickity reads cellAlign
        // physically, so 'left' left a short row (fewer cards than the screen) hugging the left
        // edge on /ar. Was: dataset.align == 'center' ? 'center' : 'left'
        align = dataset.align == 'center' ? 'center' : (document.dir === 'rtl' ? 'right' : 'left'),
        fade = dataset.fade == 'true',
        prev_button = slideshow.querySelector('.flickity-prev'),
        next_button = slideshow.querySelector('.flickity-next'),
        custom_dots = slideshow.querySelector('.flickity-page-dots'),
        progress_bar = slideshow.parentNode.querySelector('.flickity-progress--bar'),
        animations = [],
        rightToLeft = document.dir === 'rtl',
        animations_enabled = document.body.classList.contains('animations-true') && typeof gsap !== 'undefined';

      // Cache classList checks — avoids repeated DOM reads throughout constructor
      const isImageTextImage = slideshow.classList.contains('image-with-text-slideshow__image');
      const isImageTextContent = slideshow.classList.contains('image-with-text-slideshow__content');
      const isTestimonials = slideshow.classList.contains('testimonials__carousel');
      const isCustomerReviewsContent = slideshow.classList.contains('customer-reviews__content');
      const isCustomerReviewsImage = slideshow.classList.contains('customer-reviews__image');
      const isCustomDots = slideshow.classList.contains('custom-dots');
      const isMainSlideshow = slideshow.classList.contains('main-slideshow');
      const isShoppableReels = slideshow.classList.contains('shoppable-video-reels--carousel');
      const isProducts = slideshow.classList.contains('products');
      const isCollectionGrid = slideshow.classList.contains('collection-grid__carousel');

      slideshow._previousIndex = 0;

      const args = {
        wrapAround: true,
        cellAlign: align,
        pageDots: isCustomDots ? false : dots_enabled,
        contain: true,
        fade,
        autoPlay: autoplay,
        rightToLeft,
        prevNextButtons: false,
        cellSelector: '.carousel__slide',
        on: {}
      };

      // Reduced motion: land on the chosen slide at once instead of gliding.
      if (reduce) {
        args.selectedAttraction = 1;
        args.friction = 1;
      }

      if (isImageTextImage) {
        const image_slideshow_slides = slideshow.querySelectorAll('.image-with-text-slideshow__image-media');
        args.draggable = false;
        args.asNavFor = slideshow.parentNode.querySelector('.image-with-text-slideshow__content');
        if (image_slideshow_slides.length && image_slideshow_slides[0].classList.contains('desktop-height-auto')) {
          args.adaptiveHeight = true;
        }
      }
      if (isCustomerReviewsImage) {
        args.draggable = false;
        args.asNavFor = slideshow.parentNode.querySelector('.customer-reviews__content');
      }
      if (isImageTextContent || isTestimonials || isCustomerReviewsContent || isCustomerReviewsImage) {
        args.adaptiveHeight = true;
      }
      // Autoplaying reviews keep the tallest review's height: resizing to
      // each one would move the page below on every advance. Read from the
      // setting, so the editor (which doesn't autoplay) shows the same.
      if (isCustomerReviewsContent && dataset.autoplay != 'false') {
        args.adaptiveHeight = false;
      }

      // Cache dot elements once — prevents querySelectorAll on every slide change
      const cached_dot_list = custom_dots ? Array.from(custom_dots.querySelectorAll('li')) : null;

      if (isCustomDots) {
        if (animations_enabled && isMainSlideshow) {
          this.prepareAnimations(slideshow, animations);
        }
        args.pauseAutoPlayOnHover = false;

        args.on = {
          staticClick: function () {
            this.unpausePlayer();
          },
          ready: function () {
            let flkty = this;

            if (animations_enabled && isMainSlideshow) {
              slideshow.animateSlides(0, animations, true);
              gsap.set(slideshow.querySelectorAll('.star-rating,.subheading,.split-text,.button'), { visibility: 'visible' });
            }

            if (dots_enabled && cached_dot_list) {
              cached_dot_list.forEach((dot, i) => {
                dot.addEventListener('click', () => flkty.select(i));
              });
              cached_dot_list[this.selectedIndex].classList.add('is-selected');
            }

            document.fonts.ready.then(() => requestAnimationFrame(() => flkty.resize()));

            // Video Support.
            const video_container = flkty.cells[0].element.querySelector('.slideshow__slide-video-bg');
            if (video_container) {
              const iframe = video_container.querySelector('iframe');
              const video = video_container.querySelector('video');
              if (iframe) {
                iframe.onload = () => slideshow.videoPlay(video_container);
              } else if (video) {
                video.onloadstart = () => slideshow.videoPlay(video_container);
              }
            }
          },
          change: function (index) {
            const firstCell = flkty.cells[0].element;
            if (firstCell.classList.contains('is-initial-selected')) {
              firstCell.classList.remove('is-initial-selected');
            }
            const previousIndex = fizzyUIUtils.modulo(this.selectedIndex - 1, this.slides.length),
              nextIndex = fizzyUIUtils.modulo(this.selectedIndex + 1, this.slides.length);

            // Direction detection.
            const prevIdx = slideshow._previousIndex;
            const total = this.slides.length;
            const diff = index - prevIdx;
            const isForward = diff > 0 ? (diff <= total / 2) : (Math.abs(diff) > total / 2);
            slideshow._previousIndex = index;

            // Animations.
            if (animations_enabled && isMainSlideshow) {
              slideshow.animateSlides(index, animations, isForward, previousIndex);
            }

            // Color changes — deferred to avoid forced reflow during change callback
            const selectedElement = this.selectedElement;
            requestAnimationFrame(() => {
              const cs = getComputedStyle(selectedElement);
              slideshow.style.setProperty('--color-body', cs.getPropertyValue('--color-body'));
              slideshow.style.setProperty('--color-body-rgb', cs.getPropertyValue('--color-body-rgb'));
            });

            // Custom Dots.
            if (dots_enabled && cached_dot_list) {
              cached_dot_list.forEach((dot) => dot.classList.remove('is-selected'));
              cached_dot_list[this.selectedIndex].classList.add('is-selected');
            }

            // AutoPlay — stop immediately, restart after animation completes
            if (autoplay) {
              this.stopPlayer();
              if (!(animations_enabled && isMainSlideshow && slideshow.dataset.transition == 'swipe')) {
                this.playPlayer();
              }
            }

            // Video Support.
            // previous slide
            const video_container_prev = flkty.cells[previousIndex].element.querySelector('.slideshow__slide-video-bg');
            if (video_container_prev) {
              slideshow.videoPause(video_container_prev);
            }
            // next slide
            const video_container_next = flkty.cells[nextIndex].element.querySelector('.slideshow__slide-video-bg');
            if (video_container_next) {
              slideshow.videoPause(video_container_next);
            }
            // current slide
            const video_container = flkty.cells[index].element.querySelector('.slideshow__slide-video-bg');
            if (video_container) {
              const iframe = video_container.querySelector('iframe');
              if (iframe) {
                // A later slide's player opens the first time its slide
                // shows (slideshow.liquid keeps its URL in data-src).
                if (iframe.dataset.src) {
                  iframe.addEventListener('load', () => slideshow.videoPlay(video_container), { once: true });
                  theme.loadNow(iframe);
                } else {
                  slideshow.videoPlay(video_container);
                }
              } else if (video_container.querySelector('video')) {
                slideshow.videoPlay(video_container);
              }
            }
          }
        };
      }
      if (isShoppableReels) {
        args.on.staticClick = function (event, pointer, cellElement, cellIndex) {
          if (typeof cellIndex == 'number') {
            flkty.select(cellIndex);
          }
        };
      }
      if (isMainSlideshow) {
        if (slideshow.classList.contains('desktop-height-image') || slideshow.classList.contains('mobile-height-image')) {
          args.adaptiveHeight = true;
        }
      }
      if (isProducts || isCollectionGrid) {
        args.wrapAround = false;
        args.on.ready = function () {
          if (next_button && isProducts) {
            let resizeRaf = 0;
            slideshow._resizeHandler = function () {
              cancelAnimationFrame(resizeRaf);
              resizeRaf = requestAnimationFrame(() => {
                slideshow.centerArrows(slideshow._flkty, prev_button, next_button);
              });
            };
            window.addEventListener('resize', slideshow._resizeHandler);
          }
          window.dispatchEvent(new Event('resize'));
        };
      }
      if (progress_bar) {
        let scrollRaf = 0;
        args.wrapAround = false;
        args.on.scroll = function (progress) {
          cancelAnimationFrame(scrollRaf);
          scrollRaf = requestAnimationFrame(() => {
            progress = Math.max(0, Math.min(1, progress));
            progress_bar.style.width = progress * 100 + '%';
          });
        };
      }
      const flkty = new Flickity(slideshow, args);

      // Store references for cleanup in disconnectedCallback
      this._flkty = flkty;
      this._animations = animations;
      this._animations_enabled = animations_enabled && isMainSlideshow;

      dataset.initiated = true;

      // The arrows are <button>s, so Enter and Space arrive as clicks.
      if (prev_button) {
        prev_button.addEventListener('click', () => flkty.previous());
        next_button.addEventListener('click', () => flkty.next());
      }

      if (Shopify.designMode) {
        slideshow.addEventListener('shopify:block:select', (event) => {
          const index = slideshow_slides.indexOf(event.target);
          flkty.select(index);
        });
      }

      // Product hotspots, which only the main slideshow's slides have.
      if (isMainSlideshow && slideshow.querySelector('.slideshow__hotspots')) {
        this.setupHotspots(flkty, !!autoplay);
      }
    }
    // The hotspots of the main slideshow (slideshow.liquid):
    // - the pins on slides that aren't selected are inert;
    // - autoplay waits while a pin has the pointer or focus, and while the
    //   quick view a pin opened is loading or open;
    // - a click that ends a drag doesn't open quick view. Flickity cancels
    //   it at its viewport, after a pin's <quick-view> would have taken it,
    //   so it is stopped here first, on the way down.
    // The pins fade in after the image's zoom (prepareAnimations).
    setupHotspots(flkty, autoplay) {
      const slideshow = this;
      const frames = flkty.cells.map((cell) => cell.element.querySelector(':scope > .slideshow__hotspots'));
      const controller = new AbortController();
      const signal = controller.signal;
      this._hotspotsController = controller;

      const select = () => {
        frames.forEach((frame) => {
          if (!frame) return;
          const off = frame.parentElement !== flkty.selectedElement;
          // Focus on a pin that goes inert would drop to <body>.
          if (off && frame.contains(document.activeElement)) slideshow.focus({ preventScroll: true });
          frame.inert = off;
        });
      };
      select();
      flkty.on('change', select);

      slideshow.addEventListener('click', (event) => {
        if (flkty.isPreventingClicks && event.target.closest('.slideshow__hotspots')) {
          event.preventDefault();
          event.stopPropagation();
        }
      }, { capture: true, signal });

      if (!autoplay) return;

      let hovered = false;
      // The pin's <quick-view> that took a click (QuickView in app.js).
      let opened = null;
      const held = () => {
        if (hovered || frames.some((frame) => frame && frame.contains(document.activeElement))) return true;
        if (!opened) return false;
        const drawer = document.getElementById('Product-Drawer');
        return opened.classList.contains('loading') || !!(drawer && drawer.classList.contains('active'));
      };
      const sync = () => {
        if (held()) {
          flkty.pausePlayer();
        } else {
          flkty.unpausePlayer();
        }
      };
      // Every restart of autoplay (after a slide change, the swipe's wipe
      // or a hidden tab) waits too while a pin is in use.
      const player = flkty.player;
      const play = player.play;
      player.play = function () {
        play.call(this);
        if (held()) this.pause();
      };

      // Flickity stops autoplay on any press, and only a slide change
      // starts it again ('change' above). A click on a pin is a click on
      // the product, not on the slideshow: autoplay is held instead (and
      // released as below), unless a click elsewhere on the slide had
      // already stopped it. A press that turns into a drag changes the
      // slide, as before.
      let stoppedByClick = false;
      flkty.on('staticClick', (event) => {
        if (!(event.target.closest && event.target.closest('.slideshow__hotspots .product-hotspot'))) {
          stoppedByClick = true;
        } else if (!stoppedByClick) {
          flkty.playPlayer();
        }
      });
      flkty.on('change', () => {
        stoppedByClick = false;
      });

      const pointer = (event) => {
        const target = event.type === 'pointerover' ? event.target : event.relatedTarget;
        const value = !!(target && target.closest && slideshow.contains(target) && target.closest('.product-hotspot'));
        if (value === hovered) return;
        hovered = value;
        sync();
      };
      slideshow.addEventListener('pointerover', pointer, { signal });
      slideshow.addEventListener('pointerout', pointer, { signal });
      slideshow.addEventListener('focusin', sync, { signal });
      slideshow.addEventListener('focusout', sync, { signal });
      // QuickView prevents the click's default when it opens (or starts
      // loading) the quick view; a modifier click it leaves to the link.
      slideshow.addEventListener('click', (event) => {
        const trigger = event.defaultPrevented && event.target.closest('.slideshow__hotspots quick-view');
        if (!trigger) return;
        opened = trigger;
        sync();
      }, { signal });
      document.addEventListener('panel:closed', (event) => {
        if (!opened || !event.detail || !event.detail.panel || event.detail.panel.id !== 'Product-Drawer') return;
        opened = null;
        sync();
      }, { signal });
    }
    disconnectedCallback() {
      // Destroy Flickity instance
      if (this._flkty) {
        this._flkty.destroy();
        this._flkty = null;
      }

      // Kill all GSAP animation timelines
      if (this._animations_enabled && this._animations) {
        this._animations.forEach((tl) => tl.kill());
        this._animations = null;
      }

      // Remove the hotspot listeners
      if (this._hotspotsController) {
        this._hotspotsController.abort();
        this._hotspotsController = null;
      }

      // Remove resize listener
      if (this._resizeHandler) {
        window.removeEventListener('resize', this._resizeHandler);
        this._resizeHandler = null;
      }

      // Restore the original markup so a re-render re-splits cleanly
      if (this._splittext) {
        this._splittext.revert();
        this._splittext = null;
      }

      // Reset animation state so prepareAnimations can re-run
      delete this.dataset.animationsReady;
      this.dataset.initiated = false;
    }
    // The player in a slide's video container: a hosted <video>, or a
    // YouTube/Vimeo <iframe>. A shoppable reel without a video has neither,
    // only a placeholder image.
    videoPlayer(video_container) {
      return video_container.querySelector(video_container.dataset.provider === 'hosted' ? 'video' : 'iframe');
    }
    videoPause(video_container) {
      setTimeout(() => {
        const player = this.videoPlayer(video_container);
        if (!player) return;
        if (player.tagName === 'VIDEO') {
          player.pause();
        } else {
          const message = player.dataset.provider === 'youtube' ? { event: "command", func: "pauseVideo", args: "" } : { method: "pause" };
          player.contentWindow.postMessage(JSON.stringify(message), "*");
        }
      }, 10);
    }
    videoPlay(video_container) {
      if (theme.reducedMotion) return;
      setTimeout(() => {
        const player = this.videoPlayer(video_container);
        if (!player) return;
        if (player.tagName === 'VIDEO') {
          player.play();
        } else {
          const message = player.dataset.provider === 'youtube' ? { event: "command", func: "playVideo", args: "" } : { method: "play" };
          player.contentWindow.postMessage(JSON.stringify(message), "*");
        }
      }, 10);
    }
    prepareAnimations(slideshow, animations) {
      if (!slideshow.dataset.animationsReady) {
        const split_text_els = slideshow.querySelectorAll('.split-text');
        slideshow._splittext = new SplitText(split_text_els, { type: 'lines, words', linesClass: 'line-child' });

        var s = 1 + (3 - (parseInt(slideshow.dataset.animationSpeed, 10) || 3)) * 0.3;

        slideshow.querySelectorAll('.slideshow__slide').forEach((item, i) => {
          // Cache all element refs per slide — avoids repeated querySelector in timeline definitions
          const bg = item.querySelector('.slideshow__slide-bg');
          const stars = item.querySelector('.star-rating');
          const subheading = item.querySelector('.subheading');
          const headingLines = item.querySelectorAll('.slideshow__slide-content--heading .line-child div');
          const bodyLines = item.querySelectorAll('p:not(.subheading) .line-child div');
          const buttons = item.querySelectorAll('.button');

          const tl = gsap.timeline({ paused: true });
          let button_offset = 0;

          animations[i] = tl;

          tl.to(bg, { duration: 1.5 * s, scale: 1, ease: 'none' }, "start");

          if (stars) {
            // The gold fill is a gradient positioned by --star-rating, so
            // tweening the variable sweeps the fill across the stars and
            // lands correctly on half-star values. Read the rating before
            // the tween is built — fromTo immediately resets it to 0.
            const rating = parseFloat(stars.style.getPropertyValue('--star-rating')) || 0;
            tl.fromTo(stars, { autoAlpha: 0 }, { duration: 0.4 * s, autoAlpha: 1 }, 0)
              .fromTo(stars, { '--star-rating': 0 }, { duration: 0.9 * s, '--star-rating': rating, ease: 'power2.out' }, 0);
          }
          if (subheading) {
            tl.to(subheading, { duration: 0.8 * s, autoAlpha: 1 }, 0);
            button_offset += 0.8 * s;
          }
          if (headingLines.length) {
            const h1_duration = 0.8 * s + ((headingLines.length - 1) * 0.08 * s);
            tl.from(headingLines, { duration: h1_duration, yPercent: '100', rotationZ: 10, stagger: 0.08 * s }, 0);
            button_offset += h1_duration;
          }
          if (bodyLines.length) {
            const p_duration = 0.8 * s + ((bodyLines.length - 1) * 0.04 * s);
            tl.from(bodyLines, { duration: p_duration, yPercent: '100', rotationZ: 10, stagger: 0.04 * s }, 0);
            button_offset += p_duration;
          }
          if (buttons.length) {
            tl.fromTo(buttons, { y: '100%' }, { duration: 0.8 * s, y: '0%', stagger: 0.15 * s }, button_offset * 0.2);
          }
          // Hotspot pins fade in once the image has zoomed (the swipe's
          // zoom; other transitions don't zoom), so they don't drift over
          // it, and go first as the slide leaves (the timeline reverses).
          const hotspots = item.querySelector(':scope > .slideshow__hotspots');
          if (hotspots) {
            tl.fromTo(hotspots, { autoAlpha: 0 }, { duration: 0.4 * s, autoAlpha: 1 }, slideshow.dataset.transition == 'swipe' ? 1.5 * s : 0);
          }
          item.dataset.timeline = tl;
        });
        slideshow.dataset.animationsReady = true;
      }
    }
    animateSlides(i, animations, isForward, prevSlideIndex) {
      var slides = this.querySelectorAll('.slideshow__slide');
      var slide = slides[i];
      var prevSlide = prevSlideIndex !== undefined ? slides[prevSlideIndex] : null;
      var s = 1 + (3 - (parseInt(this.dataset.animationSpeed, 10) || 3)) * 0.3;
      if (this.dataset.transition == 'swipe') {
        if (this._lastPrevSlide) {
          gsap.killTweensOf(this._lastPrevSlide, 'clipPath');
          gsap.set(this._lastPrevSlide, { clearProps: 'clipPath' });
        }
        this._lastPrevSlide = prevSlide;
        if (slide) {
          var self = this;
          // The wipe opens from the side the slide comes in from: the right
          // going forward in LTR, the left in RTL, where Flickity runs
          // right to left.
          var fromRight = isForward !== (document.dir === 'rtl');
          var from = fromRight ? 'polygon(100% 0%, 100% 0%, 100% 100%, 100% 100%)' : 'polygon(0% 0%, 0% 0%, 0% 100%, 0% 100%)';
          gsap.fromTo(slide, { clipPath: from }, {
            duration: 0.85 * s,
            clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)',
            ease: 'power3.inOut',
            overwrite: true,
            onComplete: function() {
              if (self._flkty && self._flkty.options.autoPlay) {
                self._flkty.playPlayer();
              }
            }
          });
        }
      }
      animations[i].timeScale(1).restart();
      if (prevSlideIndex !== undefined) {
        animations[prevSlideIndex].timeScale(3).reverse();
      }
    }
    centerArrows(flickity, prev_button, next_button) {
      // Anchor arrow centering on the first cell that has a product
      // image — cells[0] is sometimes a promotion (eg. collection-tabs)
      // which has no .product-card--featured-image, and would otherwise
      // collapse image_height to 0 and yank the arrows out of place.
      let image_height = 0;
      for (let i = 0; i < flickity.cells.length; i++) {
        const img = flickity.cells[i].element.querySelector('.product-card--featured-image');
        if (img && img.clientHeight) {
          image_height = img.clientHeight;
          break;
        }
      }
      if (!image_height) {
        // No product card in the carousel — leave the arrows at the CSS
        // default (vertically centered on the cell).
        prev_button.style.transform = '';
        next_button.style.transform = '';
        return;
      }

      let max_height = 0;
      flickity.cells.forEach((item) => {
        if (item.size.height > max_height) {
          max_height = item.size.height;
        }
      });

      if (max_height > image_height) {
        const difference = (max_height - image_height) / -2;
        prev_button.style.transform = 'translateY(' + difference + 'px)';
        next_button.style.transform = 'translateY(' + difference + 'px)';
      } else {
        prev_button.style.transform = '';
        next_button.style.transform = '';
      }
    }
  }
  customElements.define('slide-show', SlideShow);
}
